import React, { useState, useEffect } from 'react';
import { View, StyleSheet, ScrollView, Alert, TouchableOpacity } from 'react-native';
import { Text, Card, Button, ActivityIndicator, DataTable, Searchbar, Menu } from 'react-native-paper';
import PeriodoChip from '../../components/PeriodoChip';
import { Ionicons } from '@expo/vector-icons';
import { formatCurrency, formatDate } from '../../utils/formatters';
import api from '../../services/api';
import { exportService } from '../../services/exportService';

const PERIODOS = [
  { id: 'hoy', label: 'Hoy' },
  { id: 'esta_semana', label: 'Esta Semana' },
  { id: 'este_mes', label: 'Este Mes' },
];

const ReporteVentaDiariaScreen = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [filteredVentas, setFilteredVentas] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [menuVisible, setMenuVisible] = useState(false);
  const [menuVendedorVisible, setMenuVendedorVisible] = useState(false);
  const [periodoActivo, setPeriodoActivo] = useState('hoy');
  const [vendedorFiltro, setVendedorFiltro] = useState(null);
  const [vendedoresDisponibles, setVendedoresDisponibles] = useState([]);

  useEffect(() => { cargarReporte(); }, []);
  useEffect(() => { filtrarDatos(); }, [searchQuery, data]);

  const cargarReporte = async (periodo = periodoActivo, vendedor = vendedorFiltro) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ periodo });
      if (vendedor?.vendedor_id) params.append('vendedor_id', vendedor.vendedor_id);
      const response = await api.get(`/reportes/venta-diaria?${params.toString()}`);
      setData(response);
      setFilteredVentas(response.ventas || []);
      // Mantener la lista completa de vendedores solo cuando no hay filtro aplicado
      if (!vendedor && Array.isArray(response.resumen_vendedor)) {
        setVendedoresDisponibles(response.resumen_vendedor.filter(v => v.vendedor_id));
      }
    } catch (error) {
      console.error('Error:', error);
      Alert.alert('Error', 'No se pudo cargar el reporte');
      setData(null);
      setFilteredVentas([]);
    } finally {
      setLoading(false);
    }
  };

  const cambiarPeriodo = (p) => {
    setPeriodoActivo(p);
    // Al cambiar período se refresca la lista de vendedores disponibles
    setVendedorFiltro(null);
    cargarReporte(p, null);
  };

  const seleccionarVendedor = (v) => {
    setMenuVendedorVisible(false);
    setVendedorFiltro(v);
    cargarReporte(periodoActivo, v);
  };

  const filtrarDatos = () => {
    if (!data?.ventas) { setFilteredVentas([]); return; }
    if (!searchQuery.trim()) { setFilteredVentas(data.ventas); return; }
    const q = searchQuery.toLowerCase();
    setFilteredVentas(data.ventas.filter(v =>
      (v.cliente_nombre || '').toLowerCase().includes(q) ||
      (v.vendedor_nombre || '').toLowerCase().includes(q) ||
      String(v.venta_id).includes(q)
    ));
  };

  const exportarDatos = async (formato) => {
    setMenuVisible(false);
    if (!filteredVentas.length) return Alert.alert('Error', 'No hay datos para exportar');

    const headers = ['#', 'Fecha', 'Hora', 'Cliente', 'Vendedor', 'Monto', 'Método Pago', 'Estado', 'Checklist', 'Observación'];
    const exportData = filteredVentas.map(v => ({
      '#': v.venta_id,
      'Fecha': v.fecha,
      'Hora': v.hora,
      'Cliente': v.cliente_nombre || 'Consumidor Final',
      'Vendedor': v.vendedor_nombre || '-',
      'Monto': parseFloat(v.monto_factura || 0).toFixed(2),
      'Método Pago': v.metodo_pago || 'efectivo',
      'Estado': v.estado,
      'Checklist': v.items_total > 0 ? `${v.items_verificados}/${v.items_total}` : 'N/A',
      'Observación': v.observacion || '-'
    }));

    const periodoLabel = PERIODOS.find(p => p.id === periodoActivo)?.label || 'Hoy';
    const vendedorLabel = vendedorFiltro ? ` - ${vendedorFiltro.vendedor_nombre}` : '';
    const fileName = vendedorFiltro
      ? `venta_diaria_${(vendedorFiltro.vendedor_nombre || '').toLowerCase().replace(/\s+/g, '_')}`
      : 'venta_diaria';
    await exportService.exportData(formato, exportData, fileName, headers, `Reporte de Venta Diaria - ${periodoLabel}${vendedorLabel}`);
  };

  if (loading) {
    return <View style={styles.loader}><ActivityIndicator size="large" color="#0066cc" /><Text style={{ marginTop: 10, color: '#666' }}>Generando reporte...</Text></View>;
  }

  const totales = data?.totales || {};
  const resVendedor = data?.resumen_vendedor || [];
  const resPago = data?.resumen_pago || [];

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <View style={{ flex: 1 }}>
          <Text style={styles.title}>Venta Diaria</Text>
          <Text style={styles.subtitle}>
            {PERIODOS.find(p => p.id === periodoActivo)?.label}
            {vendedorFiltro ? ` · ${vendedorFiltro.vendedor_nombre}` : ''}
          </Text>
        </View>
        <Menu visible={menuVisible} onDismiss={() => setMenuVisible(false)}
          anchor={<Button icon="download" mode="outlined" onPress={() => setMenuVisible(true)}>Exportar</Button>}>
          <Menu.Item title="Excel" onPress={() => exportarDatos('excel')} />
          <Menu.Item title="PDF" onPress={() => exportarDatos('pdf')} />
          <Menu.Item title="CSV" onPress={() => exportarDatos('csv')} />
        </Menu>
      </View>

      {/* Filtro por vendedor */}
      {vendedoresDisponibles.length > 0 && (
        <View style={styles.vendedorFiltroContainer}>
          <Menu visible={menuVendedorVisible} onDismiss={() => setMenuVendedorVisible(false)}
            anchor={
              <Button icon="account-filter" mode="outlined" compact onPress={() => setMenuVendedorVisible(true)}>
                Vendedor: {vendedorFiltro ? vendedorFiltro.vendedor_nombre : 'Todos'}
              </Button>
            }>
            <Menu.Item title="Todos" onPress={() => seleccionarVendedor(null)} />
            {vendedoresDisponibles.map(v => (
              <Menu.Item key={v.vendedor_id} title={v.vendedor_nombre} onPress={() => seleccionarVendedor(v)} />
            ))}
          </Menu>
          {vendedorFiltro && (
            <Button mode="text" compact onPress={() => seleccionarVendedor(null)}>Quitar filtro</Button>
          )}
        </View>
      )}

      {/* Períodos */}
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.filtrosContainer}>
        <View style={styles.filtros}>
          {PERIODOS.map(p => (
            <PeriodoChip
              key={p.id}
              active={periodoActivo === p.id}
              onPress={() => cambiarPeriodo(p.id)}
            >
              {p.label}
            </PeriodoChip>
          ))}
        </View>
      </ScrollView>

      <ScrollView>
        {/* Totales */}
        <Card style={styles.resumenCard}>
          <Card.Content>
            <Text style={styles.resumenTitle}>Resumen General</Text>
            <View style={styles.resumenGrid}>
              <View style={styles.resumenItem}>
                <Text style={styles.resumenValue} numberOfLines={1} adjustsFontSizeToFit minimumFontScale={0.6}>{formatCurrency(totales.total_monto)}</Text>
                <Text style={styles.resumenLabel} numberOfLines={1}>Total Vendido</Text>
              </View>
              <View style={styles.resumenItem}>
                <Text style={styles.resumenValue} numberOfLines={1} adjustsFontSizeToFit minimumFontScale={0.6}>{totales.total_ventas || 0}</Text>
                <Text style={styles.resumenLabel} numberOfLines={1}>Facturas</Text>
              </View>
              <View style={styles.resumenItem}>
                <Text style={styles.resumenValue} numberOfLines={1} adjustsFontSizeToFit minimumFontScale={0.6}>{formatCurrency(totales.ticket_promedio)}</Text>
                <Text style={styles.resumenLabel} numberOfLines={1}>Ticket Promedio</Text>
              </View>
              <View style={styles.resumenItem}>
                <Text style={styles.resumenValue} numberOfLines={1} adjustsFontSizeToFit minimumFontScale={0.6}>{resVendedor.length}</Text>
                <Text style={styles.resumenLabel} numberOfLines={1}>Vendedores</Text>
              </View>
            </View>
          </Card.Content>
        </Card>

        {/* Resumen por vendedor */}
        {resVendedor.length > 0 && (
          <Card style={styles.resumenCard}>
            <Card.Content>
              <Text style={styles.resumenTitle}>Por Vendedor</Text>
              {resVendedor.map((rv, i) => (
                <View key={i} style={styles.vendedorRow}>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.vendedorNombre}>{rv.vendedor_nombre}</Text>
                    <Text style={styles.vendedorDetalle}>{rv.total_ventas} ventas</Text>
                  </View>
                  <Text style={styles.vendedorMonto}>{formatCurrency(rv.total_monto)}</Text>
                </View>
              ))}
            </Card.Content>
          </Card>
        )}

        {/* Resumen por método de pago */}
        {resPago.length > 0 && (
          <Card style={styles.resumenCard}>
            <Card.Content>
              <Text style={styles.resumenTitle}>Por Forma de Pago</Text>
              {resPago.map((rp, i) => (
                <View key={i} style={styles.vendedorRow}>
                  <View style={{ flex: 1 }}>
                    <Text style={[styles.vendedorNombre, { textTransform: 'capitalize' }]}>{rp.metodo_pago}</Text>
                    <Text style={styles.vendedorDetalle}>{rp.cantidad} transacciones</Text>
                  </View>
                  <Text style={styles.vendedorMonto}>{formatCurrency(rp.total)}</Text>
                </View>
              ))}
            </Card.Content>
          </Card>
        )}

        {/* Búsqueda */}
        <Searchbar placeholder="Buscar por cliente, vendedor..." onChangeText={setSearchQuery} value={searchQuery} style={styles.searchbar} />

        {/* Tabla de detalle */}
        <ScrollView horizontal style={{ paddingHorizontal: 16 }}>
          <DataTable style={{ backgroundColor: '#fff', minWidth: 900 }}>
            <DataTable.Header>
              <DataTable.Title style={{ minWidth: 50 }}>#</DataTable.Title>
              <DataTable.Title style={{ minWidth: 80 }}>Hora</DataTable.Title>
              <DataTable.Title style={{ minWidth: 150 }}>Cliente</DataTable.Title>
              <DataTable.Title style={{ minWidth: 120 }}>Vendedor</DataTable.Title>
              <DataTable.Title numeric style={{ minWidth: 100 }}>Monto</DataTable.Title>
              <DataTable.Title style={{ minWidth: 90 }}>Pago</DataTable.Title>
              <DataTable.Title style={{ minWidth: 80 }}>Check</DataTable.Title>
              <DataTable.Title style={{ minWidth: 150 }}>Observación</DataTable.Title>
            </DataTable.Header>
            {filteredVentas.map((v, idx) => (
              <DataTable.Row key={idx}>
                <DataTable.Cell style={{ minWidth: 50 }}>{v.venta_id}</DataTable.Cell>
                <DataTable.Cell style={{ minWidth: 80 }}>{v.hora}</DataTable.Cell>
                <DataTable.Cell style={{ minWidth: 150 }}>{v.cliente_nombre || 'Consumidor Final'}</DataTable.Cell>
                <DataTable.Cell style={{ minWidth: 120 }}>{v.vendedor_nombre || '-'}</DataTable.Cell>
                <DataTable.Cell numeric style={{ minWidth: 100 }}>{formatCurrency(v.monto_factura)}</DataTable.Cell>
                <DataTable.Cell style={{ minWidth: 90 }}>{(v.metodo_pago || 'efectivo').slice(0, 10)}</DataTable.Cell>
                <DataTable.Cell style={{ minWidth: 80 }}>
                  {v.items_total > 0 ? `${v.items_verificados}/${v.items_total}` : '-'}
                </DataTable.Cell>
                <DataTable.Cell style={{ minWidth: 150 }}>{v.observacion || '-'}</DataTable.Cell>
              </DataTable.Row>
            ))}
          </DataTable>
        </ScrollView>

        {filteredVentas.length === 0 && (
          <View style={styles.empty}>
            <Ionicons name="receipt-outline" size={50} color="#ccc" />
            <Text style={{ color: '#999', marginTop: 10 }}>No hay ventas para este período</Text>
          </View>
        )}

        <View style={{ height: 40 }} />
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f5f5f5' },
  loader: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  header: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', padding: 16, backgroundColor: '#fff', elevation: 2 },
  title: { fontSize: 20, fontWeight: 'bold', color: '#333' },
  subtitle: { fontSize: 14, color: '#666', marginTop: 2 },
  filtrosContainer: { maxHeight: 50, backgroundColor: '#fff', paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: '#eee' },
  filtros: { flexDirection: 'row', paddingHorizontal: 16 },
  chip: { marginRight: 8, backgroundColor: '#f0f0f0' },
  chipActivo: { backgroundColor: '#4CAF50' },
  chipTexto: { color: '#333' },
  chipTextoActivo: { color: '#fff', fontWeight: '600' },
  resumenCard: { margin: 16, marginBottom: 8, elevation: 2 },
  resumenTitle: { fontSize: 16, fontWeight: 'bold', marginBottom: 10 },
  resumenGrid: { flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'space-between' },
  resumenItem: { width: '48%', minHeight: 64, backgroundColor: '#f9f9f9', padding: 10, borderRadius: 5, marginBottom: 8, alignItems: 'center', justifyContent: 'center' },
  resumenValue: { fontSize: 18, fontWeight: 'bold', color: '#0066cc', textAlign: 'center' },
  resumenLabel: { fontSize: 12, color: '#666', marginTop: 4, textAlign: 'center' },
  vendedorRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: '#f0f0f0' },
  vendedorNombre: { fontSize: 14, fontWeight: '600', color: '#333' },
  vendedorDetalle: { fontSize: 12, color: '#888' },
  vendedorMonto: { fontSize: 15, fontWeight: '700', color: '#0066cc' },
  searchbar: { margin: 16, marginBottom: 8 },
  empty: { alignItems: 'center', padding: 40 },
  vendedorFiltroContainer: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 16, paddingVertical: 8, backgroundColor: '#fff', borderBottomWidth: 1, borderBottomColor: '#eee', gap: 8 },
});

export default ReporteVentaDiariaScreen;
